import React, {useCallback, useEffect, useRef, useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import useValidateProcedureById, {
  ProcedureValidationErrorType,
} from 'procedures/shared-components/validation/useValidateProcedureById';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as net from 'util/Net';

import {SelectedProcedure} from '../procedure-selection/ProcedureSearchSidepanel';

interface UseAttachProcedureToRunPlanOptions {
  procedureRunPlanId: string;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}

/**
 * Manages attaching a selected procedure's flowchart version to a procedure-less run plan.
 *
 * Shows a loading screen as soon as a procedure is staged, keeps it visible while frontend
 * validation runs, then updates the run plan and refetches. Surfaces an error toast if validation
 * fails or the backend update fails, and always clears the loading screen (including on unmount).
 *
 * Returns a callback to stage a procedure for attachment; call it after a valid drop/selection.
 */
export default function useAttachProcedureToRunPlan({
  procedureRunPlanId,
  studyId,
  procedureType,
  refetch,
}: UseAttachProcedureToRunPlanOptions): (selectedProcedure: SelectedProcedure) => void {
  const [stagedProcedure, setStagedProcedure] = useState<SelectedProcedure | null>(null);
  const validationResult = useValidateProcedureById(stagedProcedure?.procedureId ?? null);
  const isLoadingScreenVisibleRef = useRef(false);
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureSentenceCaseString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
  });
  const loadingMessage = `Adding ${procedureString} to plan...`;

  const showLoadingScreen = useCallback((): void => {
    if (isLoadingScreenVisibleRef.current) {
      return;
    }
    LoadingScreen.show(loadingMessage);
    isLoadingScreenVisibleRef.current = true;
  }, [loadingMessage]);

  const hideLoadingScreen = useCallback((): void => {
    if (!isLoadingScreenVisibleRef.current) {
      return;
    }
    LoadingScreen.hide();
    isLoadingScreenVisibleRef.current = false;
  }, []);

  const handleSelectProcedure = useCallback(
    async ({workflowFlowchartConfigVersionId}: SelectedProcedure): Promise<void> => {
      await updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        workflowFlowchartConfigVersionId,
      });
      refetch();
    },
    [procedureRunPlanId, studyId, refetch]
  );

  useEffect(() => {
    if (stagedProcedure == null || validationResult == null) {
      return;
    }
    if (
      validationResult.isValid === false &&
      validationResult.errorType === ProcedureValidationErrorType.NOT_LOADED
    ) {
      return;
    }
    if (validationResult.isValid) {
      const selectedProcedure = stagedProcedure;
      setStagedProcedure(null);
      // Intentionally don't hide the loading screen on success. Instead, wait for the unmount cleanup below
      // to avoid flashing a stale empty canvas between hiding the overlay and swapping in the new content.
      handleSelectProcedure(selectedProcedure).catch((error: unknown) => {
        hideLoadingScreen();
        if (net.isServerRequestError(error)) {
          notifications.error(
            <ToastMessage
              header={`Failed to add ${procedureString} to plan`}
              body={notifications.getFriendlyErrorAsElement(error as Response, '')}
            />
          );
          return;
        }
        throw error;
      });
      return;
    }
    setStagedProcedure(null);
    hideLoadingScreen();
    notifications.error(
      <ToastMessage
        header={`Failed to add ${procedureString} to plan`}
        body={`${procedureSentenceCaseString} contains errors. Fix errors to run the ${procedureString}.`}
      />
    );
  }, [
    stagedProcedure,
    validationResult,
    procedureString,
    procedureSentenceCaseString,
    handleSelectProcedure,
    hideLoadingScreen,
  ]);

  // Clear the loading screen on unmount if it is still visible (e.g. user navigates away during
  // validation or while the attach request is in flight).
  useEffect(
    () => () => {
      hideLoadingScreen();
    },
    [hideLoadingScreen]
  );

  return useCallback(
    (selectedProcedure: SelectedProcedure): void => {
      showLoadingScreen();
      setStagedProcedure(selectedProcedure);
    },
    [showLoadingScreen]
  );
}
